import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, Star } from 'lucide-react';
import WorkflowVisual from './ui/WorkflowVisual';
import { calendlyLinkProps } from '../lib/calendly';

const EASE = [0.16, 1, 0.3, 1] as const;

const logos = ['n8n', 'OpenAI', 'Make', 'Zapier', 'Supabase', 'HubSpot', 'Twilio', 'Slack'];

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, delay: i * 0.1, ease: EASE },
    }),
};

const Hero: React.FC = () => {
    return (
        <section id="top" className="relative min-h-screen flex items-center pt-32 pb-16 overflow-hidden">
            {/* Background layers */}
            <div className="absolute inset-0 bg-grid-fade pointer-events-none" />
            <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-brand-blue/25 rounded-full blur-[140px] opacity-40 animate-aurora pointer-events-none" />
            <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] bg-brand-cyan/15 rounded-full blur-[130px] opacity-30 animate-aurora pointer-events-none" style={{ animationDelay: '-6s' }} />
            <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] bg-brand-indigo/15 rounded-full blur-[130px] opacity-25 animate-aurora pointer-events-none" style={{ animationDelay: '-12s' }} />

            <div className="container-x relative z-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-16 items-center w-full">
                {/* Left: copy */}
                <div className="text-center lg:text-left">
                    <motion.div variants={fadeUp} custom={0} initial="hidden" animate="show">
                        <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass text-sm font-medium text-text-main">
                            <Sparkles className="w-4 h-4 text-brand-cyan" />
                            AI-powered automation studio
                            <span className="hidden sm:inline text-text-faint">·</span>
                            <span className="hidden sm:inline text-brand-cyan">Open for 2026</span>
                        </span>
                    </motion.div>

                    <motion.h1
                        variants={fadeUp}
                        custom={1}
                        initial="hidden"
                        animate="show"
                        className="mt-6 font-display text-[2.75rem] leading-[1.05] sm:text-6xl lg:text-[4.25rem] font-bold text-white tracking-tight text-balance"
                    >
                        Put your busywork
                        <br className="hidden sm:block" /> on{' '}
                        <span className="text-gradient">autopilot.</span>
                    </motion.h1>

                    <motion.p
                        variants={fadeUp}
                        custom={2}
                        initial="hidden"
                        animate="show"
                        className="mt-6 text-lg md:text-xl text-text-muted max-w-xl mx-auto lg:mx-0 leading-relaxed"
                    >
                        We design and build AI agents, workflows, and integrations that run your
                        repetitive operations 24/7 — so your team ships more and does less manual work.
                    </motion.p>

                    <motion.div
                        variants={fadeUp}
                        custom={3}
                        initial="hidden"
                        animate="show"
                        className="mt-9 flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-4"
                    >
                        <a {...calendlyLinkProps} className="btn-primary group w-full sm:w-auto">
                            Book a free automation audit
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </a>
                        <a href="#work" className="btn-ghost w-full sm:w-auto">
                            See what we've built
                        </a>
                    </motion.div>

                    {/* Trust row */}
                    <motion.div
                        variants={fadeUp}
                        custom={4}
                        initial="hidden"
                        animate="show"
                        className="mt-10 flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-5"
                    >
                        <div className="flex -space-x-2.5">
                            {['from-brand-cyan to-brand-blue', 'from-brand-orange to-brand-coral', 'from-brand-indigo to-brand-blue', 'from-emerald-400 to-brand-cyan'].map((g, i) => (
                                <span key={i} className={`w-9 h-9 rounded-full bg-gradient-to-br ${g} ring-2 ring-navy-900`} />
                            ))}
                        </div>
                        <div className="text-sm">
                            <div className="flex items-center gap-1 justify-center lg:justify-start">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="w-4 h-4 fill-brand-orange text-brand-orange" />
                                ))}
                            </div>
                            <div className="text-text-muted mt-0.5">
                                <span className="text-white font-semibold">50+ automations</span> shipped for happy clients
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Right: animated workflow */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
                    className="hidden md:block"
                >
                    <WorkflowVisual />
                </motion.div>
            </div>

            {/* Logo marquee */}
            <div className="absolute bottom-8 inset-x-0 z-10">
                <div className="container-x">
                    <p className="text-center text-xs text-text-faint uppercase tracking-[0.25em] font-semibold mb-5">
                        Built on the tools your business already trusts
                    </p>
                    <div className="relative overflow-hidden marquee-hover-pause [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
                        <div className="flex w-max animate-marquee gap-14">
                            {[...logos, ...logos].map((logo, i) => (
                                <span key={i} className="text-xl font-display font-semibold text-white/40 hover:text-white/80 transition-colors whitespace-nowrap">
                                    {logo}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
